import {Collapse, css} from '@mui/material';
import {
  ArrayUtils,
  ConfigObjectType,
  DateUtils,
  GetStylesFunction,
  IconHeaderWithSearch,
  LightComponentBox,
  LightIconButton,
  LightSplitLayout,
  LoadingSpinner,
  StringUtils,
  enumContainer,
  omitNullsy,
  useLocalStorage,
} from '@wallaby-ui/core';
import {parseISO} from 'date-fns';
import {isEqual, sortBy} from 'lodash';
import * as React from 'react';
import {useCallback} from 'react';
import {Helmet} from 'react-helmet';
import {
  AG_GRID_COMPATIBILITY,
  DynamicChunk,
  Formatters,
  JustTooltipFormatter,
  LTColumn,
  FILTER_ACTIVE_ICON_COLORS,
  LightDateRangeFilter,
  LightSavedFiltersButton,
  LightSelectFilter2,
  LightSelectFilter,
  LightSelectedFilters,
  LightTable,
  LightTextFilter,
  formatEncounterBreadcrumb,
  formatPatientBreadcrumb,
  useFormatters,
  useTableData,
} from 'src/js/common';
import {EncounterTimelineAnnotationFormatter} from 'src/js/common/components/formatters/EncounterTimelineAnnotationFormatter';
import {EncounterTimelineDocumentTypeFormatter} from 'src/js/common/components/formatters/EncounterTimelineDocumentTypeFormatter';
import {HaidiSchema, WallabyCoreSchema} from 'src/js/common/types';
import {useAppSelector} from 'src/js/store_hooks';

import {CrpInTimeChartBox} from './CrpInTimeChartBox';
import styles from './Encounter.less';

interface OwnProps {
  config: ConfigObjectType;
  chartIsLoading?: boolean;
  chartData?: WallabyCoreSchema.components['schemas']['LabResultEventChartDataV3Dto'][];
  chartRange: [string | undefined, string | undefined];
  encounterData: HaidiSchema.components['schemas']['EncounterPropertiesDto'] | undefined;
  encounterId: string;
  encounterIsLoading: boolean;
  query: any;
  changeQuery: (newValue: any) => void;
}

type Props = OwnProps;

const DEFAULT_TIMELINE_FILTERS = {};
const DEFAULT_GRAPH_FILTERS = {};
const DEFAULT_TIMELINE_SORTING = {
  field: 'date',
  direction: 'asc',
};
const DEFAULT_TIMELINE_PAGING = {};

type RowType = HaidiSchema.components['schemas']['EncounterTimelineEventDto'];
function getColumns(
  formatters: Formatters,
  encounterId: string,
  defaultSort: {field: string; direction: 'asc' | 'desc'}
): LTColumn<RowType>[] {
  const {t, tEnum, formatDate} = formatters;
  const columns: LTColumn<RowType>[] = [
    /* {
      field: 'number',
      title: '#',
      render: (_1, _2, index) => String(index),
    }, */
    {
      field: 'date',
      title: t('encounter.timelineTable.date'),
      tooltip: t('encounter.timelineTable.date.tooltip'),
      render: (data) => <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={formatDate(data?.eventDate ?? '')} />,
      sorting: true,
      width: '147px',
    },
    {
      field: 'days',
      title: t('encounter.timelineTable.days'),
      tooltip: t('encounter.timelineTable.days.tooltip'),
      render: (data) => <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={data?.daysFromEncounterStart} />,
      sorting: true, // NOTE: same as date sorting
      width: '90px',
    },
    {
      field: 'eventType',
      title: t('encounter.timelineTable.eventType'),
      tooltip: t('encounter.timelineTable.eventType.tooltip'),
      render: (data) => <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={tEnum('HaidiEventTypeEnum', data?.eventType)} />,
      width: '192px',
    },
    {
      field: 'annotation',
      title: t('encounter.timelineTable.annotation'),
      tooltip: t('encounter.timelineTable.annotation.tooltip'),
      minWidth: 180, // PDH-1589: readable floor; below it the table scrolls instead of collapsing the column
      render: (data) => (
        <EncounterTimelineAnnotationFormatter {...AG_GRID_COMPATIBILITY} data={data} encounterId={encounterId} formatters={formatters} />
      ),
    },
    {
      field: 'documentName',
      title: t('encounter.timelineTable.documentName'),
      tooltip: t('encounter.timelineTable.documentName.tooltip'),
      render: (data) => <EncounterTimelineDocumentTypeFormatter {...AG_GRID_COMPATIBILITY} data={data} />,
      width: '190px',
    },
  ];
  return columns.map((v) => ({
    ...v,
    sorting: v.sorting ?? false,
    customSort: () => 0,
    ...(v.field === defaultSort.field ? {defaultSort: defaultSort.direction} : {}),
  }));
}

export function getEncounterEventGroupKey(eventType: string): number {
  const EVENT_TYPE_KEY_MAP: Partial<Record<string, number>> = {
    HOSPITALIZATION_END: 0,
    HOSPITALIZATION_START: 1,
    TRANSFER: 2,
  };
  return EVENT_TYPE_KEY_MAP[eventType] ?? 3;
}

const EVENT_CATEGORY_ORDER: HaidiSchema.components['schemas']['HaidiEventTypeGroupEnum'][] = [
  'ADMINISTRATIVE',
  'MICROBIOLOGY',
  'CATHETER',
  'ATB',
  'URINARY',
  'RESPIRATORY',
  'BLOODSTREAM',
  'SURGICAL',
  'GASTROINTESTINAL',
  'OTHER',
];
const EVENT_CATEGORY_ORDER_INDEX: Record<string, number> = Object.fromEntries(EVENT_CATEGORY_ORDER.map((v, i) => [v, i]));
export const EncounterDetail: React.FC<Props> = (props) => {
  const {config, chartData, chartRange, encounterData, chartIsLoading = false, encounterId, encounterIsLoading, query, changeQuery} = props;
  const filters = React.useMemo(() => {
    return {
      timeline: query?.timeline ?? DEFAULT_TIMELINE_FILTERS,
      timelineSorting: query?.timelineSorting ?? DEFAULT_TIMELINE_SORTING,
      timelinePaging: query?.timelinePaging ?? DEFAULT_TIMELINE_PAGING,
      timelineScroll: query?.timelineScroll ?? 0,
      timelineFiltersOpen: query?.timelineFiltersOpen ?? false,
      graphFiltersOpen: query?.graphFiltersOpen ?? false,
      graph: query?.graph ?? DEFAULT_GRAPH_FILTERS,
    };
  }, [query]);
  const encounterHaisUrl = `${config.service.url.encounter}${encounterId}/timeline`;
  const encounterHaisParams = React.useMemo(() => {
    return {
      startDate: filters.timeline.startDate,
      endDate: filters.timeline.endDate,
      eventGroupTypes: filters.timeline?.eventGroupTypes,
      eventTypes: filters.timeline?.eventTypes,
      documentName: filters.timeline?.documentName,
    };
  }, [filters.timeline]);
  const {data, isLoading} = useTableData<any, RowType[]>(encounterHaisUrl, encounterHaisParams, 'events');
  const formatters = useFormatters();
  const {t, tEnum, formatString} = formatters;
  const columns = getColumns(formatters, encounterId, filters.timelineSorting);
  const sortedData = React.useMemo(() => {
    const dateSortDescending = filters.timelineSorting?.direction === 'desc';
    return ArrayUtils.sortedBy(
      data ?? [],
      (v) => {
        const eventGroupKey = getEncounterEventGroupKey(v.eventType);
        return [parseISO(v.eventTimestamp ?? ''), eventGroupKey, tEnum('HaidiEventTypeEnum', v.eventType)];
      },
      [dateSortDescending, dateSortDescending, false]
    );
  }, [data, filters.timelineSorting?.direction, tEnum]);

  // annotation text is composed on FE side -> filter on FE side
  const annotationTextFilter: string | undefined = filters.timeline?.annotationText;
  const annotationFilteredSortedData = React.useMemo(() => {
    if (!annotationTextFilter) return sortedData;
    const needle = StringUtils.normalize(annotationTextFilter);
    return sortedData.flatMap((event) => {
      const defaultAnnotation = event.eventType === 'SURGERY' ? t('encounter.timelineTable.annotation.missingSurgeryProtocol') : '';
      const matchingAnnotations = (event.annotations ?? []).filter((a) => {
        const annotationText = StringUtils.normalize(a.annotation || defaultAnnotation);
        const tooltipText = StringUtils.normalize(a.annotationTooltip || '');
        return annotationText.includes(needle) || tooltipText.includes(needle);
      });
      if (matchingAnnotations.length === 0) return [];
      return [{...event, annotations: matchingAnnotations}];
    });
  }, [sortedData, annotationTextFilter, t]);

  const filteredChartData = React.useMemo(() => {
    const startDateFilter: string | undefined = filters.graph?.dateFrom;
    const endDateFilter: string | undefined = filters.graph?.dateTo;
    return (chartData ?? []).map((v) => {
      return {
        ...v,
        dataPoints: v.dataPoints.filter((w) => {
          const timestampDate = DateUtils.dateTimeStringToDateString(w.timestamp);
          return (!startDateFilter || timestampDate >= startDateFilter) && (!endDateFilter || timestampDate <= endDateFilter);
        }),
      };
    });
  }, [chartData, filters]);

  const sortByMessageKey = (options) => sortBy(options, [(v) => t(v.messageKey)]);
  const sortByCategoryOrder = (options) => sortBy(options, [(v) => EVENT_CATEGORY_ORDER_INDEX[v.name] ?? EVENT_CATEGORY_ORDER.length]);
  const enumMap = useAppSelector((state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap);

  const storageIdPrefix = `/encounter/${encounterId}`;
  const [timelineDefaultScroll, setTimelineDefaultScroll] = useLocalStorage(`${storageIdPrefix}.timelineDefaultScroll`, 0);
  const getScrollTop = (event: Event) => (event.target as HTMLDivElement).scrollTop;
  const timelineOnScroll = (event: Event) => {
    setTimelineDefaultScroll(getScrollTop(event));
  };
  const [timelineDefaultDynamicChunks, setTimelineDefaultDynamicChunks] = useLocalStorage(
    `${storageIdPrefix}.timelineDynamicChunks`,
    [] as DynamicChunk[]
  );
  const timelineDynamicChunksOnChange = useCallback(
    (newDynamicChunks: DynamicChunk[]) => {
      setTimelineDefaultDynamicChunks(newDynamicChunks);
    },
    [setTimelineDefaultDynamicChunks]
  );

  const muiStyles = getStyles();
  const hasGraphFilters = !isEqual(omitNullsy(filters.graph), omitNullsy(DEFAULT_GRAPH_FILTERS));
  const hasTimelineFilters = !isEqual(omitNullsy(filters.timeline), omitNullsy(DEFAULT_TIMELINE_FILTERS));
  const isChartsHidden = encounterData?.encounterType === 'OUTPATIENT';
  const chartOrEncounterIsLoading = chartIsLoading || encounterIsLoading;
  return (
    <div className={styles.dlEncounter} data-name={'encounter-wrapper'}>
      <Helmet
        title={formatString(t('encounter.pageTitle'), {
          patient: formatPatientBreadcrumb({
            isLoading: encounterIsLoading,
            id: encounterData?.patientId,
            name: encounterData?.fullName,
            birthNumber: encounterData?.identificationNumber,
          }),
          encounterType: tEnum('EncounterTypeEnum', encounterData?.encounterType),
          encounter: formatEncounterBreadcrumb(
            {
              isLoading: encounterIsLoading,
              id: encounterId,
              encounterType: encounterData?.encounterType,
              orgShortName: encounterData?.admissionDepartment,
              startDate: encounterData?.encounterStartedAt,
              endDate: encounterData?.encounterEndedAt,
              isDeleted: encounterData?.encounterIsDeleted,
              isEscort: encounterData?.encounterIsEscort,
            },
            formatters
          ),
        })}
      />
      <LightSplitLayout
        initSizes={[392, 544]}
        minSizes={[56, 56]}
        storageId='/SplitLayout.Encounter.Charts-Timeline'
        isVertical
        hidden={[isChartsHidden, false]}
      >
        <div data-name='charts-wrapper' style={{overflow: 'hidden'}}>
          <LightComponentBox isOverflow={false} style={{height: '100%', gap: 8}}>
            <div className={styles.dlEncounterChartWrapper}>
              <IconHeaderWithSearch iconName={'laboratoryIconHaidi'} label={'hai.encounter.chart.crpInTime.caption'} childrenWrapperStyle={{gap: 6}}>
                <LightSelectedFilters
                  align='flex-end'
                  width='1px'
                  buttonType='chip-filled'
                  hasFilters={hasGraphFilters}
                  handleResetFilters={() => {
                    changeQuery({
                      graph: DEFAULT_GRAPH_FILTERS,
                    });
                  }}
                />
                <LightIconButton
                  iconName='filterIcon'
                  useFixedViewBox='0 0 20 16'
                  style={{width: 20, height: 16}}
                  title={filters.graphFiltersOpen ? 'layout.filters.closeFilters' : 'layout.filters.openFilters'}
                  onClick={() =>
                    changeQuery({
                      graphFiltersOpen: !filters.graphFiltersOpen,
                    })
                  }
                  selected={filters.graphFiltersOpen}
                  colors={hasGraphFilters && !filters.graphFiltersOpen ? FILTER_ACTIVE_ICON_COLORS : undefined}
                />
                <LightSavedFiltersButton
                  storageIdPrefix={`${storageIdPrefix}.graph`}
                  component='encounterLabCharts'
                  config={config}
                  filter={filters.graph}
                  setFilter={(newFilter) => changeQuery({graph: newFilter})}
                />
              </IconHeaderWithSearch>
              <div style={{display: 'flex'}}>
                <Collapse in={filters.graphFiltersOpen} timeout={500} style={{minHeight: 'none', flexGrow: 1}}>
                  <div css={muiStyles.filteringContainerWrapper}>
                    <LightDateRangeFilter
                      config={{
                        name: 'dateRange',
                        ui: {label: 'encounter.graph.date'},
                      }}
                      value={
                        filters.graph.dateFrom && filters.graph.dateTo
                          ? {
                              startDate: filters.graph.dateFrom,
                              endDate: filters.graph.dateTo,
                              shortcut: filters.graph.floatingShortcutName,
                            }
                          : null
                      }
                      onChange={(newValue) => {
                        changeQuery({
                          graph: {
                            ...filters.graph,
                            dateFrom: newValue?.startDate,
                            dateTo: newValue?.endDate,
                            floatingShortcutName: newValue?.shortcut,
                          },
                        });
                      }}
                      dateOnly
                      showShortcuts
                      isLabResults
                    />
                  </div>
                </Collapse>
              </div>
            </div>
            <div style={{height: 0, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
              {chartOrEncounterIsLoading ? (
                <LoadingSpinner isVisible text='document.loading' marginTop={-50} />
              ) : (
                <div className={styles.dlEncounterContentWrapper} data-name={'detailWrapper'} style={{width: '100%', height: '100%'}}>
                  <CrpInTimeChartBox
                    splitData={filteredChartData}
                    chartRange={[filters.graph.dateFrom ?? chartRange[0], filters.graph.dateTo ?? chartRange[1]]}
                    encounterData={encounterData}
                    encounterId={encounterId}
                  />
                </div>
              )}
            </div>
          </LightComponentBox>
        </div>
        <LightComponentBox data-name='timeline-wrapper'>
          <div className={styles.dlEncounterTimelineWrapper}>
            <IconHeaderWithSearch iconName={'timelineIcon'} label={'encounter.timelineTable'} childrenWrapperStyle={{gap: 6}}>
              <LightSelectedFilters
                align='flex-end'
                width='1px'
                buttonType='chip-filled'
                hasFilters={!isEqual(omitNullsy(filters.timeline), omitNullsy(DEFAULT_TIMELINE_FILTERS))}
                handleResetFilters={() => {
                  changeQuery({
                    timeline: DEFAULT_TIMELINE_FILTERS,
                    timelinePaging: {
                      ...filters.timelinePaging,
                      page: 0,
                    },
                  });
                  setTimelineDefaultScroll(0);
                }}
                hasSorting={!isEqual(filters.timelineSorting, DEFAULT_TIMELINE_SORTING)}
                handleResetSorting={() => {
                  changeQuery({
                    timelineSorting: DEFAULT_TIMELINE_SORTING,
                  });
                  setTimelineDefaultScroll(0);
                }}
              />
              <LightIconButton
                iconName='filterIcon'
                useFixedViewBox='0 0 20 16'
                style={{width: 20, height: 16}}
                title={filters.timelineFiltersOpen ? 'layout.filters.closeFilters' : 'layout.filters.openFilters'}
                onClick={() =>
                  changeQuery({
                    timelineFiltersOpen: !filters.timelineFiltersOpen,
                  })
                }
                selected={filters.timelineFiltersOpen}
                colors={hasTimelineFilters && !filters.timelineFiltersOpen ? FILTER_ACTIVE_ICON_COLORS : undefined}
              />
              <LightSavedFiltersButton
                storageIdPrefix={`${storageIdPrefix}.timeline`}
                component='encounterTimeline'
                config={config}
                filter={filters.timeline}
                setFilter={(newFilter) => {
                  changeQuery({
                    timeline: newFilter,
                    timelinePaging: {...filters.timelinePaging, page: 0},
                  });
                  setTimelineDefaultScroll(0);
                }}
              />
            </IconHeaderWithSearch>
            <div style={{display: 'flex'}}>
              <Collapse in={filters.timelineFiltersOpen} timeout={500} style={{minHeight: 'none', flexGrow: 1}}>
                <div css={muiStyles.filteringContainerWrapper}>
                  <LightDateRangeFilter
                    config={{
                      name: 'dateRange',
                      ui: {label: 'encounter.timelineTable.date'},
                    }}
                    value={
                      filters.timeline.startDate && filters.timeline.endDate
                        ? {
                            startDate: filters.timeline.startDate,
                            endDate: filters.timeline.endDate,
                            shortcut: filters.timeline.floatingShortcutName,
                          }
                        : null
                    }
                    onChange={(newValue) => {
                      changeQuery({
                        timeline: {
                          ...filters.timeline,
                          startDate: newValue?.startDate,
                          endDate: newValue?.endDate,
                          floatingShortcutName: newValue?.shortcut,
                        },
                        timelinePaging: {
                          ...filters.timelinePaging,
                          page: 0,
                        },
                      });
                      setTimelineDefaultScroll(0);
                    }}
                    dateOnly
                    showShortcuts
                  />
                  <LightSelectFilter
                    config={{
                      name: 'eventGroupTypes',
                      ui: {label: 'encounter.timelineTable.eventCategory'},
                    }}
                    options={sortByCategoryOrder(enumMap.HaidiEventTypeGroupEnum ?? [])}
                    multi
                    value={filters.timeline.eventGroupTypes}
                    onChange={(newValue) => {
                      changeQuery({
                        timeline: {
                          ...filters.timeline,
                          eventGroupTypes: newValue,
                        },
                        timelinePaging: {
                          ...filters.timelinePaging,
                          page: 0,
                        },
                      });
                      setTimelineDefaultScroll(0);
                    }}
                  />
                  <LightSelectFilter
                    config={{
                      name: 'eventTypes',
                      ui: {label: 'encounter.timelineTable.eventType'},
                    }}
                    options={sortByMessageKey(enumMap.HaidiEventTypeEnum ?? [])}
                    multi
                    value={filters.timeline.eventTypes}
                    onChange={(newValue) => {
                      changeQuery({
                        timeline: {
                          ...filters.timeline,
                          eventTypes: newValue,
                        },
                        timelinePaging: {
                          ...filters.timelinePaging,
                          page: 0,
                        },
                      });
                      setTimelineDefaultScroll(0);
                    }}
                  />
                  <LightTextFilter
                    config={{
                      name: 'annotationText',
                      ui: {label: 'encounter.timelineTable.annotation'},
                    }}
                    value={filters.timeline.annotationText ?? null}
                    onChange={(newValue) => {
                      changeQuery({
                        timeline: {
                          ...filters.timeline,
                          annotationText: newValue ?? undefined,
                        },
                        timelinePaging: {
                          ...filters.timelinePaging,
                          page: 0,
                        },
                      });
                      setTimelineDefaultScroll(0);
                    }}
                  />
                  <LightTextFilter
                    config={{
                      name: 'documentName',
                      ui: {label: 'encounter.timelineTable.documentName'},
                    }}
                    value={filters.timeline.documentName ?? null}
                    onChange={(newValue) => {
                      changeQuery({
                        timeline: {
                          ...filters.timeline,
                          documentName: newValue ?? undefined,
                        },
                        timelinePaging: {
                          ...filters.timelinePaging,
                          page: 0,
                        },
                      });
                      setTimelineDefaultScroll(0);
                    }}
                  />
                </div>
              </Collapse>
            </div>
            <div className={styles.dlEncounterTimeline} data-name={'caseTimelineWrapper'}>
              <LightTable
                key={encounterId}
                alignTop
                columns={columns}
                data={annotationFilteredSortedData}
                isLoading={isLoading}
                onOrderChange={(field, direction) => {
                  changeQuery({
                    timelineSorting: {
                      field,
                      direction,
                    },
                  });
                }}
                controlledSort={(() => {
                  return filters.timelineSorting;
                })()}
                defaultScroll={{
                  to: timelineDefaultScroll,
                  dependOn: null,
                }}
                onScroll={timelineOnScroll}
                useDynamicRows
                chunkSize={30}
                defaultDynamicChunks={timelineDefaultDynamicChunks}
                dynamicChunksOnChange={timelineDynamicChunksOnChange}
              />
            </div>
          </div>
        </LightComponentBox>
      </LightSplitLayout>
    </div>
  );
};

EncounterDetail.displayName = 'EncounterDetail';

const getStyles: GetStylesFunction = (options) => ({
  filteringContainerWrapper: css`
    width: 100%;
    display: flex;
    column-gap: 10px;
    justify-content: flex-start;
    align-items: flex-start;
    flex-wrap: wrap;
  `,
});
